import * as AuthSession from 'expo-auth-session'
import { auth0Config } from '@/config/auth0'
import { useSessionStore } from './useSessionStore'
import type { RootStore } from '../root.store'
import { Platform } from 'react-native'

export async function refreshAccessToken(
  tokenConfig: AuthSession.TokenResponseConfig,
  rootStore: RootStore
): Promise<AuthSession.TokenResponseConfig> {
  const { isRefreshing, addToQueue, setRefreshing, processQueue, clearQueue } = useSessionStore.getState()

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve) => {
      addToQueue((token) => resolve(token))
    })
  }

  setRefreshing(true)

  try {
    // Check if token is managed by Auth0 SPA SDK (web only)
    if (Platform.OS === 'web' && tokenConfig.refreshToken === 'managed_by_auth0_spa_sdk') {
      // Dynamic import to avoid issues on native
      const { getAuth0Client } = await import('@/hooks/useSilentSignIn')
      const auth0Client = getAuth0Client()

      // Use Auth0 SPA SDK to refresh
      const accessToken = await auth0Client.getTokenSilently({
        cacheMode: 'off',
        authorizationParams: {
          audience: auth0Config.audience,
          scope: auth0Config.scopes.join(' ')
        }
      })

      const idTokenClaims = await auth0Client.getIdTokenClaims()

      const newTokenConfig: AuthSession.TokenResponseConfig = {
        accessToken: accessToken,
        idToken: idTokenClaims?.__raw,
        tokenType: 'bearer',
        expiresIn: idTokenClaims?.exp ? idTokenClaims.exp - Math.floor(Date.now() / 1000) : 86400,
        issuedAt: Math.floor(Date.now() / 1000),
        scope: auth0Config.scopes.join(' '),
        state: '',
        refreshToken: 'managed_by_auth0_spa_sdk'
      }

      useSessionStore.getState().setTokenConfig(newTokenConfig)
      processQueue(newTokenConfig)
      return newTokenConfig
    }

    // Standard refresh token flow (native apps)
    const tokenResponse = new AuthSession.TokenResponse(tokenConfig)

    const refreshConfig: AuthSession.RefreshTokenRequestConfig = {
      clientId: auth0Config.clientId,
      refreshToken: tokenResponse.refreshToken
    }

    const endpointConfig: Pick<AuthSession.DiscoveryDocument, 'tokenEndpoint'> = {
      tokenEndpoint: `${auth0Config.discoveryUrl}/oauth/token`
    }

    const newTokenResponse = await tokenResponse.refreshAsync(refreshConfig, endpointConfig)
    const newTokenConfig = newTokenResponse.getRequestConfig()

    // Update Zustand store
    useSessionStore.getState().setTokenConfig(newTokenConfig)

    // Resolve all queued requests
    processQueue(newTokenConfig)

    return newTokenConfig
  } catch (error: any) {
    clearQueue()

    // Only logout for authentication errors, not network errors
    if (
      error?.name === 'TypeError' ||
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('Failed to fetch')
    ) {
      console.warn('Network error during token refresh, not logging out', error)
      throw error
    }

    console.error('Error refreshing token', error)
    rootStore.session.logout()
    throw error
  } finally {
    setRefreshing(false)
  }
}
